import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import BackButton from '../../components/common/BackButton';

// ── Image compression ─────────────────────────────────────────
const compressImage = (file, maxSizeMB = 1, maxDimension = 1080) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > height) {
          if (width > maxDimension) { height = (height * maxDimension) / width; width = maxDimension; }
        } else {
          if (height > maxDimension) { width = (width * maxDimension) / height; height = maxDimension; }
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        let quality = 0.8;
        const tryCompress = () => {
          canvas.toBlob((blob) => {
            if (blob.size > maxSizeMB * 1024 * 1024 && quality > 0.3) {
              quality -= 0.1;
              tryCompress();
            } else {
              resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
                type: 'image/jpeg', lastModified: Date.now()
              }));
            }
          }, 'image/jpeg', quality);
        };
        tryCompress();
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export default function AddStagePage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [form, setForm]               = useState({ stageName:'', description:'' });
  const [mediaFiles, setMediaFiles]   = useState([]);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [uploading, setUploading]     = useState(false);
  const [loading, setLoading]         = useState(false);

  const handleMedia = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = 5 - mediaFiles.length;
    if (remaining <= 0) {
      toast.error('Maximum 5 photos allowed. Remove one first.');
      e.target.value = ''; return;
    }

    const filesToUpload = files.slice(0, remaining);
    if (files.length > remaining)
      toast(`Only uploading ${remaining} photo${remaining > 1 ? 's' : ''} (max 5 total)`, { icon: 'ℹ️' });

    // File type check
    const invalid = filesToUpload.filter(f => !f.type.startsWith('image/') && !f.type.startsWith('video/'));
    if (invalid.length > 0) {
      toast.error(`❌ Invalid file: ${invalid[0].name}\nOnly JPEG, PNG, WebP, MP4 allowed.`);
      e.target.value = ''; return;
    }

    // Size check
    const tooLarge = filesToUpload.filter(f => f.size > 30 * 1024 * 1024);
    if (tooLarge.length > 0) {
      toast.error(`📁 File too large (max 30MB):\n${tooLarge[0].name}`, { duration: 4000 });
      e.target.value = ''; return;
    }

    setUploading(true);
    setUploadProgress(filesToUpload.map(f => ({
      name:   f.name.length > 20 ? f.name.slice(0, 20) + '...' : f.name,
      status: 'compressing',
      origMB: (f.size / 1024 / 1024).toFixed(1),
      compMB: null,
    })));

    const uploadedFiles = [];

    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];

      setUploadProgress(p => p.map((item, idx) =>
        idx === i ? { ...item, status: 'compressing' } : item
      ));

      let fileToUpload = file;
      if (file.type.startsWith('image/')) {
        try { fileToUpload = await compressImage(file, 1, 1080); }
        catch { fileToUpload = file; }
      }

      const compMB = (fileToUpload.size / 1024 / 1024).toFixed(1);

      setUploadProgress(p => p.map((item, idx) =>
        idx === i ? { ...item, status: 'uploading', compMB } : item
      ));

      let url = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const fd = new FormData();
          fd.append('files', fileToUpload);
          const { data } = await api.post('/upload/media', fd, {
            timeout: 120000,
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          url = data.files?.[0]?.url || null;
          break;
        } catch (err) {
          if (attempt === 3) {
            setUploadProgress(p => p.map((item, idx) =>
              idx === i ? { ...item, status: 'failed' } : item
            ));
            let msg = '❌ Upload failed. Try again.';
            if (err.code === 'ECONNABORTED' || err.message?.includes('timeout'))
              msg = '⏱️ Upload timed out. Check your internet.';
            else if (err.message?.includes('Network Error'))
              msg = '📶 No internet connection.';
            else if (err.response?.status === 413)
              msg = '📁 File too large for server.';
            else if (err.response?.data?.message)
              msg = err.response.data.message;
            toast.error(msg, { duration: 5000 });
          } else {
            await new Promise(r => setTimeout(r, 1500 * attempt));
          }
        }
      }

      if (url) {
        const preview = await new Promise(resolve => {
          const r = new FileReader();
          r.onload = ev => resolve(ev.target.result);
          r.readAsDataURL(file);
        });
        uploadedFiles.push({ preview, serverUrl: url });
        setUploadProgress(p => p.map((item, idx) =>
          idx === i ? { ...item, status: 'done' } : item
        ));
      }
    }

    if (uploadedFiles.length > 0) {
      setMediaFiles(p => [...p, ...uploadedFiles]);
      toast.success(`${uploadedFiles.length} photo${uploadedFiles.length > 1 ? 's' : ''} added! ✅`);
    }

    setUploading(false);
    setTimeout(() => setUploadProgress([]), 2500);
    e.target.value = '';
  };

  const submit = async () => {
    if (mediaFiles.length === 0) return toast.error('At least 1 photo required');
    setLoading(true);
    try {
      const { data } = await api.post(`/crops/${id}/stages`, {
        ...form,
        mediaUrls: mediaFiles.map(f => f.serverUrl)
      });
      toast.success(`Stage ${data.stage.stageNumber} added! ✅`);
      navigate(`/crop/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving stage');
    } finally { setLoading(false); }
  };

  return (
    <div className="page">
      <div className="header header--simple">
        <BackButton />
        <div className="header__title">Add Stage Update</div>
      </div>

      <div className="container" style={{ paddingTop:'var(--sp-4)' }}>

        {/* ── Media Upload ── */}
        <div className="card" style={{ marginBottom:'var(--sp-4)' }}>
          <div className="card__body">
            <label className="label">📷 Photos / Video *</label>
            <div className="media-grid" style={{ marginTop:'var(--sp-2)', marginBottom:'var(--sp-2)' }}>
              {mediaFiles.map((file, i) => (
                <div key={i} className="media-thumb">
                  <img src={file.preview} alt="" />
                  <button className="media-thumb__rm"
                    onClick={() => setMediaFiles(p => p.filter((_,j) => j !== i))}>×</button>
                </div>
              ))}
              {mediaFiles.length < 5 && (
                <label className={`media-upload-btn media-upload-btn--camera${uploading ? ' media-upload-btn--disabled' : ''}`}>
                  {uploading ? <span className="spinner spinner--sm spinner--white" />
                    : <><span>📷</span><span className="media-upload-btn__label">Camera</span></>}
                  <input type="file" accept="image/*" capture="environment"
                    style={{ display:'none' }} onChange={handleMedia} disabled={uploading} />
                </label>
              )}
              {mediaFiles.length < 5 && (
                <label className={`media-upload-btn media-upload-btn--gallery${uploading ? ' media-upload-btn--disabled' : ''}`}>
                  {uploading ? <span className="spinner spinner--sm" />
                    : <><span>🖼️</span><span className="media-upload-btn__label">Gallery</span></>}
                  <input type="file" accept="image/*,video/mp4" multiple
                    style={{ display:'none' }} onChange={handleMedia} disabled={uploading} />
                </label>
              )}
            </div>

            {/* ── Upload Progress ── */}
            {uploadProgress.length > 0 && (
              <div style={{ marginBottom:8 }}>
                {uploadProgress.map((item, i) => (
                  <div key={i} style={{
                    display:'flex', alignItems:'center', gap:8,
                    padding:'6px 10px', borderRadius:8, marginBottom:4,
                    background: item.status==='done' ? '#F0FDF4' : item.status==='failed' ? '#FEF2F2' : '#F8FAFC',
                    border: `1px solid ${item.status==='done' ? '#BBF7D0' : item.status==='failed' ? '#FECACA' : '#E2E8F0'}`,
                  }}>
                    <span style={{ fontSize:15 }}>
                      {item.status==='compressing' ? '⚙️' : item.status==='uploading' ? '📤'
                        : item.status==='done' ? '✅' : '❌'}
                    </span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:'#1A6B3C', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {item.name}
                      </div>
                      <div style={{ fontSize:11, color:'#64748B' }}>
                        {item.status==='compressing' ? `Compressing ${item.origMB}MB...`
                          : item.status==='uploading' ? `Uploading ${item.compMB}MB... (was ${item.origMB}MB)`
                          : item.status==='done' ? `✓ Done — ${item.origMB}MB → ${item.compMB}MB`
                          : 'Failed — try again'}
                      </div>
                    </div>
                    {(item.status==='compressing' || item.status==='uploading') && (
                      <div style={{ width:14, height:14, border:'2px solid #E2E8F0', borderTopColor:'#1A6B3C', borderRadius:'50%', animation:'spin 0.7s linear infinite', flexShrink:0 }} />
                    )}
                  </div>
                ))}
              </div>
            )}

            <p className="hint">
              📷 Camera · 🖼️ Gallery · Max 5 · Auto-compressed
              {mediaFiles.length > 0 && <span style={{ color:'#1A6B3C', fontWeight:600 }}> · {mediaFiles.length}/5 added</span>}
            </p>
          </div>
        </div>

        {/* ── Stage Details ── */}
        <div className="card" style={{ marginBottom:'var(--sp-4)' }}>
          <div className="card__body">
            <div className="form-group">
              <div>
                <label className="label">Stage Name <span style={{ color:'#94A3B8', fontSize:12 }}>(optional)</span></label>
                <input className="input" placeholder="e.g. Flowering Stage (leave blank for auto)"
                  value={form.stageName}
                  onChange={e => setForm(p => ({ ...p, stageName: e.target.value }))} />
              </div>
              <div>
                <label className="label">Description <span style={{ color:'#94A3B8', fontSize:12 }}>(optional)</span></label>
                <textarea className="input" rows={4}
                  placeholder="What changed? Any issues, observations, or actions taken..."
                  value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
              </div>
            </div>
          </div>
        </div>

        <button className="btn btn--primary btn--lg" onClick={submit}
          disabled={loading || uploading}
          style={{ opacity: loading || uploading ? 0.7 : 1 }}>
          {loading
            ? <><span className="spinner spinner--sm spinner--white" /> Saving...</>
            : '✅ Save Stage Update'}
        </button>
        <div style={{ height:'var(--sp-6)' }} />
      </div>
    </div>
  );
}